import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
import { useConversationTracker } from "./hooks/use-conversation-tracker";
import { AudioRecorder } from "./lib/audio-recorder";
import AudioVisualizer from "./components/AudioVisualizer";
import SimpleVisualizer from "./components/SimpleVisualizer";
import InfoModal from "./components/InfoModal";
import SettingsPanel, { VisualizerMode } from "./components/SettingsPanel";
import AuthGate from "./components/AuthGate";
import {
  GeminiVoice,
  isOpenAIRealtimeVoice,
  OpenAIRealtimeVoice,
  OPENAI_REALTIME_VOICES,
} from "./components/voices";
import {
  AgentHubJob,
  agentHubStateLabel,
  isTerminalAgentHubState,
  mergeAgentHubJob,
  normalizeAgentHubJob,
  submittedAgentHubJobResult,
} from "./agentHubStatus";
import {
  ActionStateEvent,
  ContextUsageEvent,
  InteractionMode,
  isOpenAIRealtimeRuntime,
  isVoiceRuntime,
  RuntimeConfig,
  SessionConfigEvent,
  SessionEndEvent,
  SessionEndState,
  StartupMode,
  ToolResultEvent,
  ToolStartEvent,
  TranscriptEvent,
  VoiceRuntime,
  WebSourcesEvent,
} from "./types";
import "./App.scss";

type DebugEvent = {
  ts: string;
  type: "assistant" | "tool" | "system";
  text: string;
};

type TranscriptEntry = {
  id: number;
  role: "user" | "assistant";
  text: string;
  partial: boolean;
  ts: string;
};

type ChatToolCall = {
  name?: string;
  args?: Record<string, unknown>;
  ok?: boolean;
  result?: unknown;
  error?: string;
  summary?: string;
};

type ChatResponse = {
  ok?: boolean;
  reply?: string;
  conversation_id?: string;
  tool_calls?: ChatToolCall[];
  persisted?: boolean;
  trace_id?: string;
  web_searches?: WebSourcesEvent[];
};

type AidaThread = {
  document_id?: string;
  external_id?: string;
  title?: string;
  summary?: string | null;
  started_at?: string | null;
  last_interaction_at?: string | null;
  message_count?: number;
};

type AidaConversationState = {
  ok?: boolean;
  diagnostic?: {
    active_conversation?: {
      conversation_id?: string;
      client_session_id?: string | null;
      interaction_mode?: string | null;
      current_voice?: string | null;
      last_activity_at?: string | null;
      continuity_expires_at?: string | null;
      document_id?: string | null;
      metadata?: {
        last_contact?: {
          surface?: string | null;
          channel?: string | null;
          channel_id?: string | null;
        };
      };
    } | null;
  };
  recent_threads?: AidaThread[];
};

const APP_VERSION = process.env.REACT_APP_VERSION || "dev";
const APP_REVISION = process.env.REACT_APP_REVISION || "local";
const APP_BUILD_LABEL = `v${APP_VERSION} / ${APP_REVISION}`;
const ESTIMATED_CONTEXT_WINDOW_TOKENS = 128000;
const ESTIMATED_CONTEXT_COMPRESSION_TRIGGER_TOKENS = Math.round(
  ESTIMATED_CONTEXT_WINDOW_TOKENS * 0.8
);
const ESTIMATED_BASE_PROMPT_TOKENS = 1200;

function formatThreadDate(value?: string | null) {
  if (!value) return "No activity yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity yet";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getThreadTitle(thread?: AidaThread | null) {
  if (!thread) return "No active thread";
  return thread.title?.trim() || thread.summary?.trim()?.slice(0, 80) || "Untitled thread";
}

function getSurfaceBadges(thread?: AidaThread | null) {
  const summary = `${thread?.summary || ""} ${thread?.title || ""}`.toLowerCase();
  const badges = new Set<string>();
  if (summary.includes("slack")) badges.add("slack");
  if (summary.includes("voice")) badges.add("voice");
  if (summary.includes("chat")) badges.add("chat");
  return Array.from(badges);
}

function estimateTokenCount(text: string) {
  const normalized = text.trim();
  if (!normalized) return 0;
  return Math.ceil(normalized.length / 4);
}

function buildEstimatedContextUsage(
  workingMemory: string,
  transcriptEntries: TranscriptEntry[]
): ContextUsageEvent {
  const transcriptText = transcriptEntries.map((entry) => entry.text).join("\n");
  const totalTokenCount =
    ESTIMATED_BASE_PROMPT_TOKENS +
    estimateTokenCount(workingMemory) +
    estimateTokenCount(transcriptText);
  return {
    source: "estimate",
    promptTokenCount: totalTokenCount,
    responseTokenCount: null,
    totalTokenCount,
    contextWindowTokens: ESTIMATED_CONTEXT_WINDOW_TOKENS,
    compressionTriggerTokens: ESTIMATED_CONTEXT_COMPRESSION_TRIGGER_TOKENS,
    percentUsed: Math.min(
      100,
      Math.round((totalTokenCount / ESTIMATED_CONTEXT_WINDOW_TOKENS) * 1000) / 10
    ),
  };
}

function getContextUsageLevel(usage: ContextUsageEvent) {
  if (usage.totalTokenCount >= usage.compressionTriggerTokens || usage.percentUsed >= 80) {
    return "danger";
  }
  if (usage.percentUsed >= 60) {
    return "warn";
  }
  return "safe";
}

function formatTokenCount(value: number) {
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return String(value);
}

function formatContextUsageTitle(usage: ContextUsageEvent) {
  const source = usage.source === "gemini" ? "Gemini Live" : "local estimate";
  return `${source}: ${usage.percentUsed.toFixed(1)}% (${formatTokenCount(
    usage.totalTokenCount
  )}/${formatTokenCount(usage.contextWindowTokens)} tokens)`;
}

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  audioInitialBufferMs: 500,
  outputPrimeOnConnect: true,
  carStartTestEnabled: true,
  availableVoiceRuntimes: ["gemini_live"],
  openAIRealtimeVoices: OPENAI_REALTIME_VOICES.map((voice) => voice.id),
  voiceRuntimeAvailabilityKnown: false,
};

type AgentHubArtifact = {
  id: string;
  status: "pending" | "ready" | "rejected" | "expired";
  filename: string;
  content_type: string;
  byte_size: number;
  kind: "file" | "text" | "json" | "url" | "diagnostic";
  sha256?: string;
};

type AgentHubUploadSession = {
  artifact: AgentHubArtifact;
  upload_url: string;
  method: "PUT";
  headers?: Record<string, string>;
};

async function sha256File(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function formatArtifactSize(bytes?: number) {
  if (!bytes || bytes < 1) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

function AgentHubJobCard({ job }: { job: AgentHubJob }) {
  const statusCopy =
    job.state === "queued"
      ? "Your request is waiting for Codex."
      : job.state === "working"
      ? job.progress || "Codex is working on your request."
      : job.state === "needs_input"
      ? job.progress || "Codex needs an answer before it can continue."
      : job.state === "awaiting_approval"
      ? job.progress || "Codex is waiting for an approval."
      : job.state === "failed"
      ? job.error || "Codex could not complete this request."
      : job.result || "Codex completed this request.";

  return (
    <article
      className={`agent-hub-job-card state-${job.state}`}
      aria-live={isTerminalAgentHubState(job.state) || job.state === "needs_input" ? "polite" : "off"}
      aria-label={`Codex task: ${agentHubStateLabel(job.state)}`}
    >
      <div className="agent-hub-job-header">
        <span className="agent-hub-job-status-mark" aria-hidden="true" />
        <div>
          <div className="agent-hub-job-eyebrow">Codex task</div>
          <div className="agent-hub-job-state">{agentHubStateLabel(job.state)}</div>
        </div>
      </div>
      {job.title && <div className="agent-hub-job-title">{job.title}</div>}
      <div className="agent-hub-job-copy">{statusCopy}</div>
      {job.pollError && (
        <div className="agent-hub-job-poll-error">
          Status is temporarily unavailable. AIDA will keep trying while this chat is open.
        </div>
      )}
      {job.resultArtifacts.length > 0 && (
        <div className="agent-hub-job-artifacts">
          <div className="agent-hub-job-artifacts-label">Result files</div>
          <ul>
            {job.resultArtifacts.map((artifact) => (
              <li key={artifact.id}>
                <span>{artifact.filename}</span>
                {artifact.byte_size ? <small>{formatArtifactSize(artifact.byte_size)}</small> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      <details className="agent-hub-job-details">
        <summary>Details</summary>
        <div>Job ID: {job.jobId}</div>
      </details>
    </article>
  );
}

async function waitForStatusRetry(delayMs: number) {
  await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
}

async function fetchAgentHubJobStatus(jobId: string) {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch("/api/tools/get_agent_job_status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail =
          data && typeof data === "object" && typeof data.detail === "string"
            ? data.detail
            : `Status request failed: ${response.status}`;
        throw new Error(detail);
      }
      return data;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt === 0) await waitForStatusRetry(1000);
    }
  }
  throw lastError || new Error("Status request failed");
}

type AidaInterfaceProps = {
  runtimeConfig: RuntimeConfig;
};

function AidaInterface({ runtimeConfig }: AidaInterfaceProps) {
  const {
    client,
    connected,
    connecting,
    recovering,
    connect,
    disconnect,
    waitForPlaybackIdle,
    setPlaybackMuted,
    analyser,
    lastCloseCode,
    lastCloseReason,
  } = useLiveAPIContext();
  const { sessionId, addMessage, addToolCall, flushConversation, startNewSession } =
    useConversationTracker();

  const [workingMemory, setWorkingMemory] = useState("");
  const [lastToolCall, setLastToolCall] = useState("");
  const [pendingTools, setPendingTools] = useState<Record<string, ToolStartEvent>>({});
  const activeTool = Object.values(pendingTools)[0]?.name || null;
  const [toolError, setToolError] = useState<string | null>(null);
  const [awaitingAssistantResponse, setAwaitingAssistantResponse] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<GeminiVoice>("Leda");
  const [selectedOpenAIRealtimeVoice, setSelectedOpenAIRealtimeVoice] =
    useState<OpenAIRealtimeVoice>(() => {
      const saved = localStorage.getItem("aidaOpenAIRealtimeVoice");
      return isOpenAIRealtimeVoice(saved) ? saved : "marin";
    });
  const [selectedVoiceRuntime, setSelectedVoiceRuntime] = useState<VoiceRuntime>("gemini_live");
  const availableOpenAIRealtimeVoices = runtimeConfig.openAIRealtimeVoices.filter(
    (voice): voice is OpenAIRealtimeVoice => isOpenAIRealtimeVoice(voice)
  );
  useEffect(() => {
    if (!runtimeConfig.voiceRuntimeAvailabilityKnown) {
      return;
    }
    const preferred = localStorage.getItem("aidaVoiceRuntime");
    if (isVoiceRuntime(preferred) && runtimeConfig.availableVoiceRuntimes.includes(preferred)) {
      setSelectedVoiceRuntime(preferred);
      return;
    }
    setSelectedVoiceRuntime("gemini_live");
    if (preferred && preferred !== "gemini_live") {
      localStorage.setItem("aidaVoiceRuntime", "gemini_live");
    }
  }, [runtimeConfig.availableVoiceRuntimes, runtimeConfig.voiceRuntimeAvailabilityKnown]);
  const [interactionMode, setInteractionMode] = useState<InteractionMode>("voice");
  const [standaloneChatActive, setStandaloneChatActive] = useState(false);
  const [standaloneChatError, setStandaloneChatError] = useState<string | null>(null);
  const [textInput, setTextInput] = useState("");
  const [codingArtifacts, setCodingArtifacts] = useState<AgentHubArtifact[]>([]);
  const [agentHubJobs, setAgentHubJobs] = useState<AgentHubJob[]>([]);
  const [artifactUploading, setArtifactUploading] = useState(false);
  const [artifactError, setArtifactError] = useState<string | null>(null);
  const artifactInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const pendingCodingArtifactIdsRef = useRef<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [sessionCapturePaused, setSessionCapturePaused] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [startupMode, setStartupMode] = useState<StartupMode>("normal");
  const [carStartEnabled, setCarStartEnabled] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [visualizerMode, setVisualizerMode] = useState<VisualizerMode>(() => {
    return (localStorage.getItem("visualizerMode") as VisualizerMode) || "full";
  });
  const [showDebugTranscript, setShowDebugTranscript] = useState<boolean>(() => {
    return localStorage.getItem("showDebugTranscript") === "true";
  });
  const [debugEvents, setDebugEvents] = useState<DebugEvent[]>([]);
  const [transcriptEntries, setTranscriptEntries] = useState<TranscriptEntry[]>([]);
  const [contextUsage, setContextUsage] = useState<ContextUsageEvent | null>(null);
  const [webSources, setWebSources] = useState<WebSourcesEvent[]>([]);
  const [searchMemoryLimitInfo, setSearchMemoryLimitInfo] = useState<{
    limit: number;
    mode: "default" | "override";
  } | null>(null);
  const [showThreadDrawer, setShowThreadDrawer] = useState(false);
  const [conversationState, setConversationState] = useState<AidaConversationState | null>(null);
  const [conversationStateLoading, setConversationStateLoading] = useState(false);
  const [conversationStateError, setConversationStateError] = useState<string | null>(null);
  const [switchingDocumentId, setSwitchingDocumentId] = useState<string | null>(null);
  const [workingMemoryLoaded, setWorkingMemoryLoaded] = useState(false);
  const [statusMessage, setStatusMessage] = useState("disconnected");
  const [sessionEndState, setSessionEndState] = useState<SessionEndState | "idle">("idle");
  const [actionState, setActionState] = useState<ActionStateEvent | null>(null);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const activeAgentHubJobIds = useMemo(
    () =>
      agentHubJobs
        .filter((job) => !isTerminalAgentHubState(job.state))
        .map((job) => job.jobId)
        .sort()
        .join(","),
    [agentHubJobs]
  );

  const upsertAgentHubJob = useCallback((rawJob: unknown) => {
    const incoming = normalizeAgentHubJob(rawJob);
    if (!incoming) return;
    setAgentHubJobs((current) => {
      const existingIndex = current.findIndex((job) => job.jobId === incoming.jobId);
      if (existingIndex < 0) return [...current, incoming].slice(-5);
      const next = [...current];
      next[existingIndex] = mergeAgentHubJob(next[existingIndex], incoming);
      return next;
    });
  }, []);

  useEffect(() => {
    const jobIds = activeAgentHubJobIds ? activeAgentHubJobIds.split(",") : [];
    if (jobIds.length === 0) return;
    let cancelled = false;
    const timers = new Map<string, number>();

    const poll = async (jobId: string) => {
      try {
        const result = await fetchAgentHubJobStatus(jobId);
        if (cancelled) return;
        upsertAgentHubJob(result);
        const job = normalizeAgentHubJob(result);
        if (job && isTerminalAgentHubState(job.state)) return;
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        setAgentHubJobs((current) =>
          current.map((job) =>
            job.jobId === jobId ? { ...job, pollError: message } : job
          )
        );
      }
      if (!cancelled) {
        timers.set(jobId, window.setTimeout(() => void poll(jobId), 4000));
      }
    };

    jobIds.forEach((jobId) => {
      timers.set(jobId, window.setTimeout(() => void poll(jobId), 1200));
    });
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [activeAgentHubJobIds, upsertAgentHubJob]);

  const estimatedContextUsage = useMemo(
    () => buildEstimatedContextUsage(workingMemory, transcriptEntries),
    [transcriptEntries, workingMemory]
  );
  const contextUsageView = contextUsage || estimatedContextUsage;
  const contextUsageLevel = getContextUsageLevel(contextUsageView);

  const audioStreamActiveRef = useRef(false);
  const silenceTimerRef = useRef<number | null>(null);
  const speechDetectedRef = useRef(false);
  const lastVoiceAtRef = useRef(0);
  const listeningGraceUntilRef = useRef(0);
  const firstAudioChunkSentRef = useRef(false);
  const voiceDetectedLoggedRef = useRef(false);
  const pendingToolsRef = useRef<Record<string, ToolStartEvent>>({});
  const initialTurnCompletedRef = useRef(false);
  const forceNewNextConnectRef = useRef(false);
  const carStartupReadySentRef = useRef(false);
  const firstSpeechAfterConnectPendingRef = useRef(true);
  const firstCaptureAfterConnectPendingRef = useRef(true);
  const transcriptIdRef = useRef(0);
  const inputTranscriptRef = useRef("");
  const outputTranscriptRef = useRef("");
  const committedInputRef = useRef("");
  const committedOutputRef = useRef("");
  const loggedInputDebugRef = useRef("");
  const loggedOutputDebugRef = useRef("");
  const standaloneChatEndRef = useRef<HTMLDivElement | null>(null);
  const sessionEndStateRef = useRef<SessionEndState | "idle">("idle");
  const sessionEndClosingRef = useRef(false);

  const pushDebugEvent = useCallback((event: Omit<DebugEvent, "ts">) => {
    const next: DebugEvent = {
      ...event,
      ts: new Date().toISOString(),
    };
    setDebugEvents((prev) => [...prev.slice(-79), next]);
  }, []);

  const sendClientTelemetryEvent = useCallback(
    (eventType: string, payload: Record<string, unknown> = {}, severity: "info" | "warn" | "error" = "info") => {
      void fetch("/api/client-events", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        keepalive: true,
        body: JSON.stringify({
          client_session_id: sessionId,
          events: [
            {
              event_type: eventType,
              severity,
              payload,
            },
          ],
        }),
      }).catch(() => {
        // Ignore telemetry delivery failures.
      });
    },
    [sessionId]
  );

  const handleVisualizerModeChange = (mode: VisualizerMode) => {
    setVisualizerMode(mode);
    localStorage.setItem("visualizerMode", mode);
  };

  const handleDebugTranscriptToggle = (enabled: boolean) => {
    setShowDebugTranscript(enabled);
    localStorage.setItem("showDebugTranscript", String(enabled));
  };

  const appendTranscriptChunk = useCallback((current: string, chunk: string) => {
    const nextChunk = chunk || "";
    if (!nextChunk.trim()) {
      return current;
    }

    if (!current) {
      return nextChunk.trim();
    }

    if (nextChunk.startsWith(current)) {
      return nextChunk.trim();
    }

    if (current.endsWith(nextChunk)) {
      return current;
    }

    return `${current}${nextChunk}`
      .replace(/\s+/g, " ")
      .replace(/\s([,.;!?])/g, "$1")
      .trim();
  }, []);

  const updateTranscriptEntries = useCallback(
    (role: "user" | "assistant", chunk: string, finished: boolean) => {
      if (!chunk.trim()) {
        return;
      }

      setTranscriptEntries((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === role && last.partial) {
          const nextText = appendTranscriptChunk(last.text, chunk);
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              text: nextText,
              partial: !finished,
            },
          ];
        }

        return [
          ...prev,
          {
            id: ++transcriptIdRef.current,
            role,
            text: appendTranscriptChunk("", chunk),
            partial: !finished,
            ts: new Date().toISOString(),
          },
        ];
      });
    },
    [appendTranscriptChunk]
  );

  const finalizeTranscriptEntries = useCallback(() => {
    setTranscriptEntries((prev) =>
      prev.map((entry) => (entry.partial ? { ...entry, partial: false } : entry))
    );
  }, []);

  const loadWorkingMemory = useCallback(async () => {
    try {
      const response = await fetch("/api/working-memory");
      if (!response.ok) {
        throw new Error(`Working memory request failed: ${response.status}`);
      }
      const data = await response.json();
      setWorkingMemory(data.content || "");
    } catch (error) {
      console.error("[AIDA] Failed to load working memory:", error);
      setWorkingMemory("");
    } finally {
      setWorkingMemoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    void loadWorkingMemory();
  }, [loadWorkingMemory]);

  const loadConversationState = useCallback(async () => {
    setConversationStateLoading(true);
    setConversationStateError(null);
    try {
      const params = new URLSearchParams({
        surface: standaloneChatActive ? "standalone_app" : "voice",
        client_session_id: sessionId,
      });
      const response = await fetch(`/api/conversation/state?${params.toString()}`, {
        credentials: "same-origin",
      });
      const data = (await response.json().catch(() => ({}))) as AidaConversationState & {
        detail?: string;
      };
      if (!response.ok || data.ok === false) {
        throw new Error(data.detail || `Conversation state failed: ${response.status}`);
      }
      setConversationState(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setConversationStateError(message);
    } finally {
      setConversationStateLoading(false);
    }
  }, [sessionId, standaloneChatActive]);

  const openThreadDrawer = useCallback(() => {
    setShowThreadDrawer(true);
    void loadConversationState();
  }, [loadConversationState]);

  const handleSwitchThread = useCallback(
    async (thread: AidaThread) => {
      if (!thread.document_id || connected || connecting) {
        return;
      }
      setSwitchingDocumentId(thread.document_id);
      setConversationStateError(null);
      try {
        const response = await fetch("/api/conversation/switch", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            document_id: thread.document_id,
            surface: standaloneChatActive ? "standalone_app" : "voice",
            client_session_id: sessionId,
          }),
        });
        const data = (await response.json().catch(() => ({}))) as {
          ok?: boolean;
          detail?: string;
        };
        if (!response.ok || data.ok === false) {
          throw new Error(data.detail || `Switch failed: ${response.status}`);
        }
        setTranscriptEntries([]);
        setContextUsage(null);
        transcriptIdRef.current = 0;
        forceNewNextConnectRef.current = false;
        setStatusMessage(`Thread ready: ${getThreadTitle(thread)}`);
        await loadConversationState();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setConversationStateError(message);
      } finally {
        setSwitchingDocumentId(null);
      }
    },
    [connected, connecting, loadConversationState, sessionId, standaloneChatActive]
  );

  const commitTranscriptIfNeeded = useCallback(
    (role: "user" | "assistant", text: string, committedRef: MutableRefObject<string>) => {
      const normalized = text.trim();
      if (!normalized || committedRef.current === normalized) {
        return;
      }
      committedRef.current = normalized;
      addMessage(role, normalized);
    },
    [addMessage]
  );

  const handleInputTranscript = useCallback(
    (event: TranscriptEvent) => {
      const chunk = event.text || "";
      inputTranscriptRef.current = appendTranscriptChunk(inputTranscriptRef.current, chunk);
      updateTranscriptEntries("user", chunk, Boolean(event.finished));
      if (event.finished) {
        commitTranscriptIfNeeded("user", inputTranscriptRef.current, committedInputRef);
        if (
          inputTranscriptRef.current.trim() &&
          loggedInputDebugRef.current !== inputTranscriptRef.current.trim()
        ) {
          loggedInputDebugRef.current = inputTranscriptRef.current.trim();
          pushDebugEvent({ type: "system", text: `user: ${inputTranscriptRef.current.trim()}` });
        }
      }
    },
    [appendTranscriptChunk, commitTranscriptIfNeeded, pushDebugEvent, updateTranscriptEntries]
  );

  const handleOutputTranscript = useCallback(
    (event: TranscriptEvent) => {
      const chunk = event.text || "";
      outputTranscriptRef.current = appendTranscriptChunk(outputTranscriptRef.current, chunk);
      updateTranscriptEntries("assistant", chunk, Boolean(event.finished));
      if (event.text?.trim()) {
        setAwaitingAssistantResponse(false);
      }
      if (event.finished) {
        commitTranscriptIfNeeded("assistant", outputTranscriptRef.current, committedOutputRef);
        if (
          outputTranscriptRef.current.trim() &&
          loggedOutputDebugRef.current !== outputTranscriptRef.current.trim()
        ) {
          loggedOutputDebugRef.current = outputTranscriptRef.current.trim();
          pushDebugEvent({ type: "assistant", text: outputTranscriptRef.current.trim() });
        }
      }
    },
    [appendTranscriptChunk, commitTranscriptIfNeeded, pushDebugEvent, updateTranscriptEntries]
  );

  const handleToolStart = useCallback(
    (event: ToolStartEvent) => {
      const args = event.args || {};
      pendingToolsRef.current = { ...pendingToolsRef.current, [event.callId]: event };
      setPendingTools(pendingToolsRef.current);
      setToolError(null);
      if (event.blocking) setAwaitingAssistantResponse(true);

      const formattedArgs = Object.keys(args).length ? ` ${JSON.stringify(args)}` : "";
      setLastToolCall(`${event.name}${formattedArgs}`);
      pushDebugEvent({ type: "tool", text: `start ${event.name}${formattedArgs}` });

      if (event.name === "search_memory") {
        const rawLimit = args.limit;
        const limit =
          typeof rawLimit === "number" ? Math.max(1, Math.floor(rawLimit)) : 10;
        setSearchMemoryLimitInfo({
          limit,
          mode: limit === 10 ? "default" : "override",
        });
      }
    },
    [pushDebugEvent]
  );

  const handleToolResult = useCallback(
    (event: ToolResultEvent) => {
      const pending = pendingToolsRef.current[event.callId];
      const remaining = { ...pendingToolsRef.current };
      delete remaining[event.callId];
      pendingToolsRef.current = remaining;
      setPendingTools(remaining);
      if (event.status === "error") {
        setToolError(event.error || "Tool call failed");
        window.setTimeout(() => setToolError(null), 5000);
      } else if (event.status === "ok") {
        setToolError(null);
        const submittedJob = submittedAgentHubJobResult(event.name, event.result);
        if (submittedJob && pendingCodingArtifactIdsRef.current.length > 0) {
          const submittedIds = new Set(pendingCodingArtifactIdsRef.current);
          setCodingArtifacts((current) =>
            current.filter((artifact) => !submittedIds.has(artifact.id))
          );
          pendingCodingArtifactIdsRef.current = [];
        }
        if (submittedJob) {
          upsertAgentHubJob(submittedJob);
        }
      }

      if (pending && event.status !== "cancelled") {
        addToolCall(event.name, pending.args || {}, event.result);
      }
      pushDebugEvent({
        type: "tool",
        text:
          event.status === "error"
            ? `error ${event.name}: ${event.error || "unknown error"}`
            : `${event.status === "cancelled" ? "cancelled" : "result"} ${event.name}`,
      });
    },
    [addToolCall, pushDebugEvent, upsertAgentHubJob]
  );

  const finishVoiceEndAfterPlayback = useCallback(async () => {
    if (sessionEndClosingRef.current) return;
    sessionEndClosingRef.current = true;
    try {
      await waitForPlaybackIdle(15000);
      await disconnect();
      setStatusMessage("Session saved · ready to continue later");
    } finally {
      sessionEndClosingRef.current = false;
    }
  }, [disconnect, waitForPlaybackIdle]);

  const handleRetrySessionSave = useCallback(() => {
    sessionEndStateRef.current = "saving";
    setSessionEndState("saving");
    setStatusMessage("Retrying conversation save...");
    client.sendSessionEndRetry();
  }, [client]);

  useEffect(() => {
    const onStatus = (event: { message: string }) => {
      setStatusMessage(event.message || "connected");
      pushDebugEvent({ type: "system", text: `status: ${event.message || "connected"}` });
    };

    const onActionState = (event: ActionStateEvent) => {
      if (event.state === "idle" || event.state === "inspected") {
        setActionState(null);
        pushDebugEvent({ type: "system", text: `action: inspected ${event.action || ""}` });
        return;
      }
      setActionState(event);
      if (event.state === "awaiting_confirmation") {
        setStatusMessage("Awaiting confirmation");
      } else if (event.state === "outcome_unknown") {
        setStatusMessage("Action outcome needs verification");
      }
      pushDebugEvent({
        type: "system",
        text: `action: ${event.state}${event.action ? ` ${event.action}` : ""}`,
      });
      if (event.state === "executed" || event.state === "canceled") {
        window.setTimeout(() => {
          setActionState((current) => (current === event ? null : current));
        }, 5000);
      }
    };

    const onTurnComplete = () => {
      if (!initialTurnCompletedRef.current || (startupMode === "car" && !micReady)) {
        initialTurnCompletedRef.current = true;
        setMicReady(true);
        pushDebugEvent({ type: "system", text: "mic: ready" });
      }
      listeningGraceUntilRef.current = Date.now() + 1500;
      firstAudioChunkSentRef.current = false;
      voiceDetectedLoggedRef.current = false;
      if (
        inputTranscriptRef.current.trim() &&
        loggedInputDebugRef.current !== inputTranscriptRef.current.trim()
      ) {
        loggedInputDebugRef.current = inputTranscriptRef.current.trim();
        pushDebugEvent({ type: "system", text: `user: ${inputTranscriptRef.current.trim()}` });
      }
      if (
        outputTranscriptRef.current.trim() &&
        loggedOutputDebugRef.current !== outputTranscriptRef.current.trim()
      ) {
        loggedOutputDebugRef.current = outputTranscriptRef.current.trim();
        pushDebugEvent({ type: "assistant", text: outputTranscriptRef.current.trim() });
      }
      commitTranscriptIfNeeded("user", inputTranscriptRef.current, committedInputRef);
      commitTranscriptIfNeeded("assistant", outputTranscriptRef.current, committedOutputRef);
      finalizeTranscriptEntries();
      setAwaitingAssistantResponse(false);
      setSessionCapturePaused(false);
      inputTranscriptRef.current = "";
      outputTranscriptRef.current = "";
      pushDebugEvent({ type: "system", text: "turn complete" });
    };

    const onInterrupted = () => {
      pushDebugEvent({ type: "system", text: "assistant interrupted" });
    };

    const onSessionResumed = () => {
      if (startupMode === "car") {
        initialTurnCompletedRef.current = false;
        setAwaitingAssistantResponse(true);
        pushDebugEvent({ type: "system", text: "session resumed; waiting for car greeting" });
        return;
      }
      initialTurnCompletedRef.current = true;
      setMicReady(true);
      setAwaitingAssistantResponse(false);
      setSessionCapturePaused(false);
      pushDebugEvent({ type: "system", text: "session resumed; mic ready" });
    };

    const onTextRejected = (event: { text: string; message: string; artifactIds: string[] }) => {
      if (event.text) {
        setTextInput(event.text);
      }
      pendingCodingArtifactIdsRef.current = [];
      setAwaitingAssistantResponse(false);
      setArtifactError(event.message);
      pushDebugEvent({ type: "system", text: `text rejected: ${event.message}` });
    };

    const onContextUsage = (event: ContextUsageEvent) => {
      setContextUsage(event);
    };

    const onSessionConfig = (event: SessionConfigEvent) => {
      pushDebugEvent({
        type: "system",
        text:
          isOpenAIRealtimeRuntime(event.voiceRuntime)
            ? `runtime: ${event.modelId || "gpt-realtime"} (reasoning ${event.reasoningEffort || "low"})`
            : `runtime: ${event.modelId || "gemini-3.8-live"}`,
      });
    };

    const onWebSources = (event: WebSourcesEvent) => {
      setWebSources((current) => [...current, event].slice(-5));
      pushDebugEvent({
        type: "system",
        text: `web search: ${event.status} (${event.sources.length} sources)`,
      });
    };

    const onSessionEnd = (event: SessionEndEvent) => {
      sessionEndStateRef.current = event.state;
      setSessionEndState(event.state);
      setStatusMessage(event.message || event.state);
      pushDebugEvent({ type: "system", text: `session end: ${event.state}` });

      if (event.state === "wrapping_up" || event.state === "saving") {
        setMuted(true);
        setSessionCapturePaused(true);
        return;
      }

      if (event.state === "saved") {
        setSessionCapturePaused(false);
        void finishVoiceEndAfterPlayback();
        return;
      }

      setSessionCapturePaused(false);
      setToolError("Conversation save failed. Retry Save or stop the session.");
    };

    const onClose = () => {
      pendingToolsRef.current = {};
      setPendingTools({});
      setStatusMessage(
        sessionEndStateRef.current === "saved"
          ? "Session saved · ready to continue later"
          : "disconnected"
      );
    };

    client
      .on("status", onStatus)
      .on("inputtranscript", handleInputTranscript)
      .on("outputtranscript", handleOutputTranscript)
      .on("toolstart", handleToolStart)
      .on("toolresult", handleToolResult)
      .on("actionstate", onActionState)
      .on("contextusage", onContextUsage)
      .on("sessionconfig", onSessionConfig)
      .on("sessionend", onSessionEnd)
      .on("websources", onWebSources)
      .on("sessionresumed", onSessionResumed)
      .on("turncomplete", onTurnComplete)
      .on("textrejected", onTextRejected)
      .on("interrupted", onInterrupted)
      .on("close", onClose);

    return () => {
      client
        .off("status", onStatus)
        .off("inputtranscript", handleInputTranscript)
        .off("outputtranscript", handleOutputTranscript)
        .off("toolstart", handleToolStart)
        .off("toolresult", handleToolResult)
        .off("actionstate", onActionState)
        .off("contextusage", onContextUsage)
        .off("sessionconfig", onSessionConfig)
        .off("sessionend", onSessionEnd)
        .off("websources", onWebSources)
        .off("sessionresumed", onSessionResumed)
        .off("turncomplete", onTurnComplete)
        .off("textrejected", onTextRejected)
        .off("interrupted", onInterrupted)
        .off("close", onClose);
    };
  }, [
    client,
    commitTranscriptIfNeeded,
    finalizeTranscriptEntries,
    handleInputTranscript,
    handleOutputTranscript,
    handleToolResult,
    handleToolStart,
    finishVoiceEndAfterPlayback,
    micReady,
    pushDebugEvent,
    startupMode,
  ]);

  useEffect(() => {
    const DEFAULT_VOLUME_THRESHOLD = 0.01;
    const POST_TURN_VOLUME_THRESHOLD = 0.01;
    const INITIAL_VOLUME_THRESHOLD = 0.008;
    const SILENCE_TIMEOUT_MS = 1200;
    const SPEECH_HANGOVER_MS = 350;
    const POST_TURN_GRACE_MS = 1500;
    const INITIAL_CAPTURE_GRACE_MS = 7000;
    const carStartupPrearm = startupMode === "car" && !micReady;
    const captureBlocked = muted || sessionCapturePaused || !micReady;

    const clearSilenceTimer = () => {
      if (silenceTimerRef.current !== null) {
        window.clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    const resetAudioCaptureState = () => {
      speechDetectedRef.current = false;
      firstAudioChunkSentRef.current = false;
      voiceDetectedLoggedRef.current = false;
      lastVoiceAtRef.current = 0;
    };

    const endAudioStream = (reason: string) => {
      if (!audioStreamActiveRef.current) {
        return;
      }
      client.sendAudioStreamEnd();
      audioStreamActiveRef.current = false;
      resetAudioCaptureState();
      pushDebugEvent({ type: "system", text: reason });
      sendClientTelemetryEvent("audio_stream_ended", { reason });
    };

    const onData = (base64: string) => {
      if (captureBlocked) {
        return;
      }

      const now = Date.now();
      const withinGraceWindow = now <= listeningGraceUntilRef.current;
      const withinSpeechWindow =
        speechDetectedRef.current || now - lastVoiceAtRef.current <= SPEECH_HANGOVER_MS;

      if (!withinSpeechWindow) {
        return;
      }

      audioStreamActiveRef.current = true;
      if (!firstAudioChunkSentRef.current) {
        firstAudioChunkSentRef.current = true;
        pushDebugEvent({
          type: "system",
          text: withinGraceWindow
            ? "first audio chunk sent (post-turn grace)"
            : "first audio chunk sent",
        });
        sendClientTelemetryEvent("audio_stream_started", {
          grace_window: withinGraceWindow,
        });
      }
      client.sendAudio(base64);
    };

    const onVolume = (volume: number) => {
      if (carStartupPrearm) {
        setInputLevel(volume);
        return;
      }

      if (captureBlocked) {
        setInputLevel(0);
        clearSilenceTimer();
        endAudioStream(sessionCapturePaused ? "audio stream paused for session save" : "audio stream paused by mute");
        resetAudioCaptureState();
        return;
      }

      const now = Date.now();
      setInputLevel(volume);
      const withinGraceWindow = now <= listeningGraceUntilRef.current;
      const activeThreshold = firstSpeechAfterConnectPendingRef.current
        ? INITIAL_VOLUME_THRESHOLD
        : withinGraceWindow
          ? POST_TURN_VOLUME_THRESHOLD
          : DEFAULT_VOLUME_THRESHOLD;

      if (volume >= activeThreshold) {
        lastVoiceAtRef.current = now;
        speechDetectedRef.current = true;
        firstSpeechAfterConnectPendingRef.current = false;
        if (!voiceDetectedLoggedRef.current) {
          voiceDetectedLoggedRef.current = true;
          pushDebugEvent({
            type: "system",
            text: `voice detected (${volume.toFixed(3)}, threshold=${activeThreshold.toFixed(3)})`,
          });
          sendClientTelemetryEvent("voice_detected", {
            volume: Number(volume.toFixed(4)),
            threshold: Number(activeThreshold.toFixed(4)),
          });
        }
        clearSilenceTimer();
        return;
      }

      if ((!speechDetectedRef.current && !audioStreamActiveRef.current) || silenceTimerRef.current !== null) {
        return;
      }

      silenceTimerRef.current = window.setTimeout(() => {
        silenceTimerRef.current = null;
        endAudioStream("audio stream ended after silence");
      }, SILENCE_TIMEOUT_MS);
    };

    const onSettings = (payload: {
      constraints?: MediaTrackConstraints;
      settings?: MediaTrackSettings;
    }) => {
      const settings = payload.settings || {};
      const settingsView = settings as MediaTrackSettings & {
        channelCount?: number;
        autoGainControl?: boolean;
        noiseSuppression?: boolean;
        echoCancellation?: boolean;
      };
      pushDebugEvent({
        type: "system",
        text:
          "mic settings: " +
          [
            `sampleRate=${settingsView.sampleRate ?? "?"}`,
            `channelCount=${settingsView.channelCount ?? "?"}`,
            `echoCancellation=${settingsView.echoCancellation ?? "?"}`,
            `noiseSuppression=${settingsView.noiseSuppression ?? "?"}`,
            `autoGainControl=${settingsView.autoGainControl ?? "?"}`,
          ].join(", "),
      });
      sendClientTelemetryEvent("mic_settings", {
        sampleRate: settingsView.sampleRate ?? null,
        channelCount: settingsView.channelCount ?? null,
        echoCancellation: settingsView.echoCancellation ?? null,
        noiseSuppression: settingsView.noiseSuppression ?? null,
        autoGainControl: settingsView.autoGainControl ?? null,
      });
    };

    if (connected && interactionMode === "voice" && (micReady || startupMode === "car")) {
      audioRecorder
        .on("settings", onSettings)
        .on("data", onData)
        .on("volume", onVolume)
        .start()
        .then(() => {
          const graceMs = firstCaptureAfterConnectPendingRef.current
            ? INITIAL_CAPTURE_GRACE_MS
            : POST_TURN_GRACE_MS;
          listeningGraceUntilRef.current = Date.now() + graceMs;
          firstCaptureAfterConnectPendingRef.current = false;
          firstAudioChunkSentRef.current = false;
          voiceDetectedLoggedRef.current = false;
          pushDebugEvent({ type: "system", text: `mic start ok (grace=${graceMs}ms)` });
          sendClientTelemetryEvent("mic_start_ok", { grace_ms: graceMs });
          if (startupMode === "car" && !carStartupReadySentRef.current) {
            carStartupReadySentRef.current = true;
            client.sendAudioRouteReady();
            client.sendCarStartRequested();
            pushDebugEvent({ type: "system", text: "car start requested; audio route ready" });
            sendClientTelemetryEvent("car_start_requested", { audio_route_ready: true });
          }
        })
        .catch((error) => {
          const message = error instanceof Error ? error.message : String(error);
          console.error("[AIDA] Failed to start recorder:", error);
          pushDebugEvent({ type: "system", text: `mic start failed: ${message}` });
          sendClientTelemetryEvent("mic_start_failed", { error: message }, "error");
        });
    } else {
      clearSilenceTimer();
      endAudioStream("audio stream ended while recorder stopped");
      resetAudioCaptureState();
      setInputLevel(0);
      audioRecorder.stop();
    }

    return () => {
      clearSilenceTimer();
      audioRecorder.off("settings", onSettings).off("data", onData).off("volume", onVolume);
    };
  }, [audioRecorder, client, connected, interactionMode, micReady, muted, pushDebugEvent, sendClientTelemetryEvent, startupMode, sessionCapturePaused]);

  useEffect(() => {
    if (connecting) {
      pushDebugEvent({ type: "system", text: "connection: connecting" });
      return;
    }

    if (connected) {
      pushDebugEvent({ type: "system", text: "connection: connected" });
      return;
    }

    const closeInfo =
      lastCloseCode != null
        ? ` (code: ${lastCloseCode}${lastCloseReason ? `, reason: ${lastCloseReason}` : ""})`
        : "";
    pushDebugEvent({ type: "system", text: `connection: disconnected${closeInfo}` });
  }, [connected, connecting, lastCloseCode, lastCloseReason, pushDebugEvent]);

  useEffect(() => {
    if (!connected && !recovering) {
      setAwaitingAssistantResponse(false);
      setMicReady(false);
      setStartupMode("normal");
      carStartupReadySentRef.current = false;
      setInputLevel(0);
      setPendingTools({});
      setSessionCapturePaused(false);
      setContextUsage(null);
      pendingToolsRef.current = {};
      audioStreamActiveRef.current = false;
      initialTurnCompletedRef.current = false;
      firstSpeechAfterConnectPendingRef.current = true;
      firstCaptureAfterConnectPendingRef.current = true;
      speechDetectedRef.current = false;
      lastVoiceAtRef.current = 0;
      listeningGraceUntilRef.current = 0;
      firstAudioChunkSentRef.current = false;
      voiceDetectedLoggedRef.current = false;
      inputTranscriptRef.current = "";
      outputTranscriptRef.current = "";
      committedInputRef.current = "";
      committedOutputRef.current = "";
      loggedInputDebugRef.current = "";
      loggedOutputDebugRef.current = "";
    }
  }, [connected, recovering]);

  useEffect(() => {
    if (connected) {
      forceNewNextConnectRef.current = false;
    }
  }, [connected]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      void flushConversation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [flushConversation]);

  const getSelectedStartupMode = (): StartupMode =>
    runtimeConfig.carStartTestEnabled && carStartEnabled ? "car" : "normal";

  const resetVoiceSessionState = () => {
    setLastToolCall("");
    pendingToolsRef.current = {};
    setPendingTools({});
    setToolError(null);
    setAwaitingAssistantResponse(false);
    setMicReady(false);
    setStandaloneChatError(null);
    setTranscriptEntries([]);
    setAgentHubJobs([]);
    setWebSources([]);
    setContextUsage(null);
    sessionEndStateRef.current = "idle";
    sessionEndClosingRef.current = false;
    setSessionEndState("idle");
    inputTranscriptRef.current = "";
    outputTranscriptRef.current = "";
    committedInputRef.current = "";
    committedOutputRef.current = "";
    loggedInputDebugRef.current = "";
    loggedOutputDebugRef.current = "";
  };

  const connectVoiceSession = async (
    nextSessionId: string,
    forceNew: boolean
  ) => {
    if (!workingMemoryLoaded) {
      return;
    }

    const nextStartupMode = getSelectedStartupMode();
    setStandaloneChatActive(false);
    setStandaloneChatError(null);
    sessionEndStateRef.current = "idle";
    sessionEndClosingRef.current = false;
    setSessionEndState("idle");
    setMuted(false);
    setInteractionMode("voice");
    setMicReady(false);
    setStartupMode(nextStartupMode);
    carStartupReadySentRef.current = false;
    setAwaitingAssistantResponse(true);

    if (nextStartupMode === "car") {
      pushDebugEvent({ type: "system", text: "car start requested" });
      sendClientTelemetryEvent("car_start_enabled_for_voice_start", { force_new: forceNew });
    }

    await connect({
      voice: selectedVoice,
      voiceRuntime: selectedVoiceRuntime,
      openAIRealtimeVoice: selectedOpenAIRealtimeVoice,
      sessionId: nextSessionId,
      startupMode: nextStartupMode,
      forceNew,
      reasoningEffort: isOpenAIRealtimeRuntime(selectedVoiceRuntime) ? "low" : undefined,
    });
    forceNewNextConnectRef.current = false;
  };

  const handleResumeSession = async () => {
    try {
      if (connected) {
        await flushConversation();
        await disconnect();
        return;
      }

      if (connecting || !workingMemoryLoaded) {
        return;
      }

      forceNewNextConnectRef.current = false;
      await connectVoiceSession(sessionId, false);
    } catch (error) {
      console.error("[AIDA] Connection error:", error);
      setAwaitingAssistantResponse(false);
    }
  };

  const handleConnect = handleResumeSession;

  const handleStartNewVoiceSession = async () => {
    try {
      if (connected || connecting || !workingMemoryLoaded) {
        return;
      }

      const nextSessionId = startNewSession();
      forceNewNextConnectRef.current = true;
      resetVoiceSessionState();
      await loadWorkingMemory();
      await connectVoiceSession(nextSessionId, true);
    } catch (error) {
      console.error("[AIDA] Start new voice session error:", error);
      setStartupMode("normal");
      setAwaitingAssistantResponse(false);
    }
  };

  const handleNewSession = async () => {
    try {
      const wasConnected = connected;
      if (wasConnected) {
        await flushConversation();
        await disconnect();
      }
      const nextSessionId = startNewSession();
      forceNewNextConnectRef.current = true;
      resetVoiceSessionState();
      await loadWorkingMemory();
      if (wasConnected) {
        await connectVoiceSession(nextSessionId, true);
      }
    } catch (error) {
      console.error("[AIDA] New session error:", error);
    }
  };

  useEffect(() => {
    if (connected) {
      setPlaybackMuted(interactionMode === "text");
    }
  }, [connected, interactionMode, setPlaybackMuted]);

  const handleStartStandaloneChat = useCallback(() => {
    if (!workingMemoryLoaded || connected || connecting) {
      return;
    }
    setStandaloneChatActive(true);
    setStandaloneChatError(null);
    setInteractionMode("text");
    setStatusMessage("chat ready");
    pushDebugEvent({ type: "system", text: "standalone chat started" });
  }, [connected, connecting, pushDebugEvent, workingMemoryLoaded]);

  const handleCloseStandaloneChat = useCallback(() => {
    setStandaloneChatActive(false);
    setStandaloneChatError(null);
    setAwaitingAssistantResponse(false);
    setTextInput("");
    setStatusMessage("disconnected");
    pushDebugEvent({ type: "system", text: "standalone chat closed" });
  }, [pushDebugEvent]);

  const handleModeSwitch = useCallback(() => {
    if (!connected) return;
    setInteractionMode((prev) => {
      const nextMode = prev === "voice" ? "text" : "voice";
      sendClientTelemetryEvent("mode_switched", { from: prev, to: nextMode });
      return nextMode;
    });
  }, [connected, sendClientTelemetryEvent]);

  const handleSelectVoiceMode = useCallback(() => {
    if (standaloneChatActive) {
      handleCloseStandaloneChat();
      setInteractionMode("voice");
      sendClientTelemetryEvent("mode_selected", { from: "standalone_chat", to: "voice" });
      return;
    }

    if (connected && interactionMode === "text") {
      setInteractionMode("voice");
      sendClientTelemetryEvent("mode_selected", { from: "text", to: "voice" });
    }
  }, [
    connected,
    handleCloseStandaloneChat,
    interactionMode,
    sendClientTelemetryEvent,
    standaloneChatActive,
  ]);

  const handleSelectChatMode = useCallback(() => {
    if (standaloneChatActive || (connected && interactionMode === "text")) {
      return;
    }

    if (connected) {
      setInteractionMode("text");
      sendClientTelemetryEvent("mode_selected", { from: "voice", to: "text" });
      return;
    }

    handleStartStandaloneChat();
  }, [
    connected,
    handleStartStandaloneChat,
    interactionMode,
    sendClientTelemetryEvent,
    standaloneChatActive,
  ]);

  const uploadCodingFile = useCallback(
    async (file: File): Promise<AgentHubArtifact> => {
      if (file.size <= 0) {
        throw new Error(`${file.name} is empty.`);
      }
      if (file.size > 25 * 1024 * 1024) {
        throw new Error(`${file.name} exceeds the 25 MB attachment limit.`);
      }
      const expectedSha256 = await sha256File(file);
      const sessionResponse = await fetch("/api/agent-hub/artifacts/upload-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          byte_size: file.size,
          kind: "file",
          surface: "web_app",
          client_session_id: sessionId,
        }),
      });
      const session = (await sessionResponse.json().catch(() => ({}))) as AgentHubUploadSession & {
        detail?: string;
      };
      if (!sessionResponse.ok || !session.upload_url || !session.artifact?.id) {
        throw new Error(session.detail || `Could not prepare ${file.name} for upload.`);
      }
      const uploadResponse = await fetch(session.upload_url, {
        method: session.method || "PUT",
        headers: {
          ...(session.headers || {}),
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed for ${file.name}: ${uploadResponse.status}`);
      }
      const completeResponse = await fetch(
        `/api/agent-hub/artifacts/${encodeURIComponent(session.artifact.id)}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ expected_sha256: expectedSha256 }),
        }
      );
      const completed = (await completeResponse.json().catch(() => ({}))) as AgentHubArtifact & {
        detail?: string;
      };
      if (!completeResponse.ok || completed.status !== "ready") {
        throw new Error(completed.detail || `Could not verify ${file.name}.`);
      }
      return completed;
    },
    [sessionId]
  );

  const handleCodingFilesSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      if (files.length === 0) return;
      const remainingSlots = Math.max(0, 20 - codingArtifacts.length);
      if (remainingSlots === 0) {
        setArtifactError("Remove an attachment before adding another (20 maximum).");
        return;
      }
      setArtifactUploading(true);
      setArtifactError(null);
      try {
        for (const file of files.slice(0, remainingSlots)) {
          const uploaded = await uploadCodingFile(file);
          setCodingArtifacts((current) =>
            current.some((artifact) => artifact.id === uploaded.id)
              ? current
              : [...current, uploaded].slice(-20)
          );
        }
      } catch (error) {
        setArtifactError(error instanceof Error ? error.message : String(error));
      } finally {
        setArtifactUploading(false);
      }
    },
    [codingArtifacts.length, uploadCodingFile]
  );

  const handleIncludeDiagnostics = useCallback(async () => {
    if (codingArtifacts.length >= 20) {
      setArtifactError("Remove an attachment before adding diagnostics (20 maximum).");
      return;
    }
    setArtifactUploading(true);
    setArtifactError(null);
    try {
      const content = JSON.stringify(
        {
          generated_at: new Date().toISOString(),
          surface: "web_app",
          session_id: sessionId,
          status: statusMessage,
          connected,
          connecting,
          selected_voice: selectedVoice,
          selected_openai_realtime_voice: selectedOpenAIRealtimeVoice,
          selected_voice_runtime: selectedVoiceRuntime,
          browser: navigator.userAgent,
          recent_debug_events: debugEvents.slice(-100),
        },
        null,
        2
      );
      const response = await fetch("/api/agent-hub/artifacts/inline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          filename: `aida-web-diagnostics-${Date.now()}.json`,
          content,
          content_type: "application/json",
          kind: "diagnostic",
          sensitivity: "sensitive",
          surface: "web_app",
          client_session_id: sessionId,
        }),
      });
      const artifact = (await response.json().catch(() => ({}))) as AgentHubArtifact & {
        detail?: string;
      };
      if (!response.ok || artifact.status !== "ready") {
        throw new Error(artifact.detail || "Could not prepare AIDA diagnostics.");
      }
      setCodingArtifacts((current) => [...current, artifact].slice(-20));
    } catch (error) {
      setArtifactError(error instanceof Error ? error.message : String(error));
    } finally {
      setArtifactUploading(false);
    }
  }, [
    connected,
    connecting,
    codingArtifacts.length,
    debugEvents,
    selectedOpenAIRealtimeVoice,
    selectedVoice,
    selectedVoiceRuntime,
    sessionId,
    statusMessage,
  ]);

  const sendStandaloneChatMessage = useCallback(
    async (message: string, artifactIds: string[] = []) => {
      setAwaitingAssistantResponse(true);
      setStandaloneChatError(null);
      setPendingTools({});
      setToolError(null);
      updateTranscriptEntries("user", message, true);
      commitTranscriptIfNeeded("user", message, committedInputRef);
      pushDebugEvent({ type: "system", text: `standalone user: ${message}` });

      const recentMessages = transcriptEntries
        .filter((entry) => !entry.partial)
        .slice(-16)
        .map((entry) => ({
          role: entry.role,
          content: entry.text,
        }));

      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            messages: [...recentMessages, { role: "user", content: message }],
            surface: "standalone_app",
            client_session_id: sessionId,
            force_new: forceNewNextConnectRef.current,
            artifact_ids: artifactIds,
          }),
        });

        const data = (await response.json().catch(() => ({}))) as ChatResponse & { detail?: string };
        if (!response.ok || data.ok === false) {
          throw new Error(data.detail || `AIDA chat failed: ${response.status}`);
        }

        const reply = (data.reply || "").trim() || "I received that, but did not get a text reply back.";
        updateTranscriptEntries("assistant", reply, true);
        commitTranscriptIfNeeded("assistant", reply, committedOutputRef);
        forceNewNextConnectRef.current = false;
        setStatusMessage("chat ready");
        pushDebugEvent({ type: "assistant", text: reply });

        const toolCalls = Array.isArray(data.tool_calls) ? data.tool_calls : [];
        const webSearches = Array.isArray(data.web_searches)
          ? data.web_searches.filter(
              (search): search is WebSourcesEvent =>
                Boolean(search) && typeof search.query === "string" && Array.isArray(search.sources)
            )
          : [];
        if (webSearches.length > 0) {
          setWebSources((current) => [...current, ...webSearches].slice(-8));
        }
        const submitted = toolCalls.some(
          (toolCall) =>
            submittedAgentHubJobResult(toolCall.name, toolCall.result) &&
            toolCall.ok !== false &&
            !toolCall.error
        );
        if (submitted && artifactIds.length > 0) {
          const sentIds = new Set(artifactIds);
          setCodingArtifacts((current) =>
            current.filter((artifact) => !sentIds.has(artifact.id))
          );
        }
        toolCalls
          .filter(
            (toolCall) =>
              submittedAgentHubJobResult(toolCall.name, toolCall.result) &&
              toolCall.ok !== false &&
              !toolCall.error
          )
          .forEach((toolCall) =>
            upsertAgentHubJob(
              submittedAgentHubJobResult(toolCall.name, toolCall.result)
            )
          );
        if (toolCalls.length > 0) {
          const lastCall = toolCalls[toolCalls.length - 1];
          const toolName = lastCall.name || "tool";
          setLastToolCall(toolName);
          toolCalls.forEach((toolCall) => {
            if (toolCall.name) {
              addToolCall(toolCall.name, toolCall.args || {}, toolCall.result || toolCall.summary || toolCall.error || {});
            }
          });
        }
      } catch (error) {
        const messageText = error instanceof Error ? error.message : String(error);
        setStandaloneChatError(messageText);
        setStatusMessage("chat error");
        pushDebugEvent({ type: "system", text: `standalone chat error: ${messageText}` });
      } finally {
        setAwaitingAssistantResponse(false);
      }
    },
    [
      addToolCall,
      commitTranscriptIfNeeded,
      pushDebugEvent,
      sessionId,
      transcriptEntries,
      upsertAgentHubJob,
      updateTranscriptEntries,
    ]
  );

  const handleSendText = useCallback(() => {
    const trimmed = textInput.trim();
    if (artifactUploading || !trimmed || (!connected && !standaloneChatActive)) return;
    setTextInput("");
    const artifactIds = codingArtifacts.map((artifact) => artifact.id);
    if (standaloneChatActive && !connected) {
      void sendStandaloneChatMessage(trimmed, artifactIds);
      return;
    }
    if (!connected) return;
    const accepted = client.sendText(trimmed, artifactIds);
    if (!accepted) {
      setTextInput(trimmed);
      setArtifactError("AIDA is reconnecting. Your message and attachments were retained.");
      return;
    }
    setAwaitingAssistantResponse(true);
    pendingCodingArtifactIdsRef.current = artifactIds;
    updateTranscriptEntries("user", trimmed, true);
    commitTranscriptIfNeeded("user", trimmed, committedInputRef);
  }, [
    artifactUploading,
    client,
    connected,
    codingArtifacts,
    standaloneChatActive,
    textInput,
    updateTranscriptEntries,
    commitTranscriptIfNeeded,
    sendStandaloneChatMessage,
  ]);

  const handleTextKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendText();
      }
    },
    [handleSendText]
  );

  const resizeTextInput = useCallback(() => {
    const input = textInputRef.current;
    if (!input) return;
    input.style.height = "auto";
    const configuredMax = Number.parseFloat(window.getComputedStyle(input).maxHeight);
    const maxHeight = Number.isFinite(configuredMax) ? configuredMax : 192;
    input.style.height = `${Math.min(input.scrollHeight, maxHeight)}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    resizeTextInput();
  }, [resizeTextInput, textInput]);

  useEffect(() => {
    window.addEventListener("resize", resizeTextInput);
    return () => window.removeEventListener("resize", resizeTextInput);
  }, [resizeTextInput]);

  const interactionReady = workingMemoryLoaded;
  const chatModeSelected = standaloneChatActive || (connected && interactionMode === "text");
  const activeConversation = conversationState?.diagnostic?.active_conversation || null;
  const activeDocumentId = activeConversation?.document_id || null;
  const recentThreads = conversationState?.recent_threads || [];
  const activeThread =
    recentThreads.find((thread) => thread.document_id && thread.document_id === activeDocumentId) || null;
  const activeSurface =
    activeConversation?.metadata?.last_contact?.surface ||
    activeConversation?.interaction_mode ||
    "unknown";
  const canSwitchThreads = !connected && !connecting;

  useEffect(() => {
    if (!standaloneChatActive) {
      return;
    }
    standaloneChatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [awaitingAssistantResponse, standaloneChatActive, transcriptEntries]);

  const getStatusText = () => {
    if (!interactionReady) return "Loading AIDA...";
    if (recovering) return "Reconnecting...";
    if (connecting) return "Connecting...";
    if (standaloneChatActive) {
      if (awaitingAssistantResponse) return "Thinking...";
      if (standaloneChatError) return "Chat error";
      return "Chat ready";
    }
    if (!connected) return "Choose how to start";
    if (activeTool === "search_memory") return "Searching memory...";
    if (activeTool) return "Processing...";
    if (sessionCapturePaused) return "Processing...";
    if (interactionMode === "text") {
      if (awaitingAssistantResponse) return "Thinking...";
      return "Text mode";
    }
    if (!micReady) return "Starting audio...";
    if (awaitingAssistantResponse) return "Processing...";
    if (muted) return "Muted";
    return "Listening...";
  };

  return (
    <div className={`aida-app mode-selector-app ${standaloneChatActive ? "standalone-chat-app" : ""}`}>
      <div className="mode-selector-wrapper" role="group" aria-label="Conversation mode">
        <div className="mode-selector">
          <button
            className={`mode-btn ${!chatModeSelected ? "active" : ""}`}
            onClick={handleSelectVoiceMode}
            aria-pressed={!chatModeSelected}
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
            Voice
          </button>
          <button
            className={`mode-btn ${chatModeSelected ? "active" : ""}`}
            onClick={handleSelectChatMode}
            disabled={!interactionReady || connecting}
            aria-pressed={chatModeSelected}
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
            </svg>
            Chat
          </button>
        </div>
      </div>

      <main className={`aida-main ${standaloneChatActive ? "standalone-chat-main" : ""}`}>
        {standaloneChatActive ? (
          <div className="standalone-chat-header">
            <div className="standalone-chat-heading">
              <h1 className="aida-title">AIDA</h1>
              <p className="status-text connected">{getStatusText()}</p>
            </div>
            <div className="standalone-chat-header-actions">
              <button
                className="standalone-chat-icon-btn"
                onClick={openThreadDrawer}
                aria-label="Open conversations"
                title="Conversations"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6.75h16M4 12h10M4 17.25h7" />
                </svg>
              </button>
              <button
                className="standalone-chat-icon-btn"
                onClick={() => setShowSettings(true)}
                aria-label="Open settings"
                title="Settings"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                className="standalone-chat-action-btn"
                onClick={handleNewSession}
                aria-label="Start new chat"
                title="Start new chat"
              >
                New
              </button>
            </div>
          </div>
        ) : (
          <h1 className="aida-title">AIDA</h1>
        )}

        {!(standaloneChatActive || (connected && interactionMode === "text")) && (
          <button
            className="visualizer-container"
            onClick={handleConnect}
            disabled={connecting || !interactionReady}
            aria-label={
              connected ? "Stop conversation" : interactionReady ? "Start conversation" : "Loading AIDA"
            }
            title={connected ? "Stop conversation" : "Continue conversation"}
          >
            {visualizerMode === "full" ? (
              <AudioVisualizer
                analyser={analyser}
                inputLevel={inputLevel}
                isConnected={connected}
                isConnecting={connecting}
                size="large"
              />
            ) : (
              <SimpleVisualizer
                analyser={analyser}
                inputLevel={inputLevel}
                isConnected={connected}
                isConnecting={connecting}
              />
            )}
          </button>
        )}

        {!standaloneChatActive && (
          <p className={`status-text ${connected ? "connected" : ""}`}>{getStatusText()}</p>
        )}

        {!connected && !standaloneChatActive && (
          <div className="start-panel" aria-label="Start AIDA voice session">
            <div className="start-actions">
              <button
                className="voice-start-btn primary"
                onClick={handleResumeSession}
                disabled={connecting || !interactionReady}
                type="button"
              >
                Continue conversation
              </button>
              <button
                className="voice-start-btn secondary"
                onClick={handleStartNewVoiceSession}
                disabled={connecting || !interactionReady}
                type="button"
              >
                New conversation
              </button>
            </div>
            <label
              className={`car-start-toggle ${carStartEnabled ? "active" : ""} ${
                !runtimeConfig.carStartTestEnabled || connecting ? "disabled" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={carStartEnabled}
                disabled={!runtimeConfig.carStartTestEnabled || connecting}
                onChange={(event) => setCarStartEnabled(event.target.checked)}
              />
              <span className="car-start-checkbox" aria-hidden="true">
                {carStartEnabled ? (
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" width="12" height="12">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 8.2l2.7 2.7 6.3-6.8" />
                  </svg>
                ) : null}
              </span>
              <span className="car-start-toggle-copy">
                <span>Car Bluetooth startup</span>
                <small>Send the audio-route warmup prompt first</small>
              </span>
            </label>
          </div>
        )}

        {activeTool && !standaloneChatActive && (
          <div className="tool-activity" role="status" aria-live="polite">
            <span className="tool-activity-icon"></span>
            <span className="tool-activity-text">
              {Object.keys(pendingTools).length > 1
                ? `${Object.keys(pendingTools).length} tasks in progress...`
                : activeTool === "search_memory"
                ? "Searching memory..."
                : activeTool === "get_working_memory"
                  ? "Loading working memory..."
                  : activeTool === "get_conversation"
                    ? "Loading conversation..."
                    : activeTool === "log_decision"
                      ? "Logging decision..."
                      : activeTool === "log_open_loop"
                        ? "Adding to Action Jackson list..."
                        : activeTool === "get_open_loops"
                          ? "Loading Action Jackson list..."
                          : activeTool === "update_open_loop"
                            ? "Updating Action Jackson item..."
                            : activeTool === "resolve_open_loop"
                              ? "Resolving Action Jackson item..."
                              : activeTool === "abandon_open_loop"
                                ? "Archiving Action Jackson item..."
                                : activeTool === "reopen_open_loop"
                                  ? "Reopening Action Jackson item..."
                        : activeTool === "add_life_list_item"
                          ? "Adding to Life List..."
                          : activeTool === "get_life_list"
                            ? "Loading Life List..."
                            : activeTool === "update_life_list_item"
                              ? "Updating Life List..."
                              : activeTool === "remove_life_list_item"
                                ? "Removing from Life List..."
                                : activeTool === "promote_life_list_item"
                                  ? "Promoting Life List item..."
                                  : activeTool === "reverse_life_list_promotion"
                                    ? "Undoing Life List promotion..."
                                : "Processing..."}
            </span>
          </div>
        )}

        {toolError && !activeTool && !standaloneChatActive && (
          <div className="tool-activity tool-error">
            <span className="tool-activity-icon">!</span>
            <span className="tool-activity-text">Tool error - check the conversation before retrying</span>
          </div>
        )}

        {actionState && !activeTool && !standaloneChatActive && (
          <div
            className={`tool-activity action-state action-state-${actionState.state}`}
            role="status"
            aria-live="polite"
          >
            <span className="tool-activity-icon">
              {actionState.state === "outcome_unknown" ? "!" : ""}
            </span>
            <span className="tool-activity-text">
              {actionState.state === "awaiting_confirmation"
                ? actionState.confirmationPrompt || "Awaiting confirmation"
                : actionState.message || actionState.state.replaceAll("_", " ")}
            </span>
            {actionState.state === "awaiting_confirmation" && actionState.confirmationDetails && (
              <details className="action-confirmation-details">
                <summary>Review exact details</summary>
                <pre>{actionState.confirmationDetails}</pre>
              </details>
            )}
          </div>
        )}

        {sessionEndState === "save_failed" && connected && (
          <div className="tool-activity tool-error">
            <span className="tool-activity-icon">!</span>
            <button type="button" className="session-save-retry" onClick={handleRetrySessionSave}>
              Retry Save
            </button>
          </div>
        )}

        {connected && (
          <div className="controls">
            {interactionMode === "voice" && (
              <button
                className={`control-btn ${muted ? "active" : ""}`}
                onClick={() => setMuted(!muted)}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                )}
              </button>
            )}

            <button
              className={`control-btn mode-toggle ${interactionMode === "text" ? "text-active" : ""}`}
              onClick={handleModeSwitch}
              aria-label={interactionMode === "voice" ? "Switch to text" : "Switch to voice"}
              title={interactionMode === "voice" ? "Switch to text" : "Switch to voice"}
            >
              {interactionMode === "voice" ? (
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                </svg>
              )}
            </button>

            <button
              className="control-btn stop"
              onClick={() => {
                void flushConversation();
                void disconnect();
              }}
              aria-label="Stop conversation"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M6 6h12v12H6z" />
              </svg>
            </button>

            <button
              className="control-btn session"
              onClick={handleNewSession}
              aria-label="Start new session"
              title="Start new session"
            >
              <span className="session-label">New</span>
            </button>
          </div>
        )}

        {standaloneChatActive && (
          <section className="standalone-chat-messages" aria-live="polite" aria-label="AIDA chat messages">
            {transcriptEntries.length === 0 && !awaitingAssistantResponse && (
              <div className="standalone-chat-empty">
                <div className="standalone-chat-message-label">AIDA</div>
                <div className="standalone-chat-message-text">Ready when you are.</div>
              </div>
            )}
            {transcriptEntries.map((entry) => (
              <div
                className={`standalone-chat-message ${entry.role}${entry.partial ? " partial" : ""}`}
                key={entry.id}
              >
                <div className="standalone-chat-message-label">{entry.role === "user" ? "You" : "AIDA"}</div>
                <div className="standalone-chat-message-text">{entry.text}</div>
              </div>
            ))}
            {awaitingAssistantResponse && (
              <div className="standalone-chat-message assistant partial">
                <div className="standalone-chat-message-label">AIDA</div>
                <div className="standalone-chat-message-text">
                  {activeTool === "search_memory" ? "Searching memory..." : "Thinking..."}
                </div>
              </div>
            )}
            <div ref={standaloneChatEndRef} />
          </section>
        )}

        {standaloneChatError && (
          <div className="tool-activity tool-error standalone-chat-error">
            <span className="tool-activity-icon">!</span>
            <span className="tool-activity-text">{standaloneChatError}</span>
          </div>
        )}

        {agentHubJobs.length > 0 && (
          <section className="agent-hub-job-list" aria-label="Codex task status">
            {agentHubJobs.map((job) => (
              <AgentHubJobCard key={job.jobId} job={job} />
            ))}
          </section>
        )}

        {(standaloneChatActive || (connected && interactionMode === "text")) && (
          <div className="coding-composer">
            {codingArtifacts.length > 0 && (
              <div className="coding-artifact-list" aria-label="Attachments for Codex">
                {codingArtifacts.map((artifact) => (
                  <span className="coding-artifact-chip" key={artifact.id}>
                    <span>{artifact.filename}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${artifact.filename}`}
                      onClick={() =>
                        setCodingArtifacts((current) =>
                          current.filter((candidate) => candidate.id !== artifact.id)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {artifactError && <div className="coding-artifact-error">{artifactError}</div>}
            <div className="text-input-container">
              <input
                ref={artifactInputRef}
                className="coding-file-input"
                type="file"
                multiple
                onChange={handleCodingFilesSelected}
              />
              <button
                className="text-utility-btn"
                onClick={() => artifactInputRef.current?.click()}
                disabled={artifactUploading}
                aria-label="Attach files for Codex"
                title="Attach files for Codex"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.4 11.6l-8.9 8.9a6 6 0 01-8.5-8.5l9.6-9.6a4 4 0 015.7 5.7l-9.6 9.6a2 2 0 01-2.8-2.8l8.9-8.9" />
                </svg>
              </button>
              <button
                className="text-utility-btn"
                onClick={() => void handleIncludeDiagnostics()}
                disabled={artifactUploading}
                aria-label="Include AIDA diagnostics for Codex"
                title="Include AIDA diagnostics for Codex"
                type="button"
              >
                {artifactUploading ? "…" : "D"}
              </button>
              <textarea
                ref={textInputRef}
                className="text-input"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={handleTextKeyDown}
                placeholder={standaloneChatActive ? "Ask AIDA anything..." : "Type a message..."}
                disabled={awaitingAssistantResponse && standaloneChatActive}
                aria-label="Message AIDA"
                rows={3}
              />
              <button
                className="text-send-btn"
                onClick={handleSendText}
                disabled={
                  !textInput.trim() ||
                  artifactUploading ||
                  (awaitingAssistantResponse && standaloneChatActive)
                }
                aria-label="Send message"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {searchMemoryLimitInfo && !standaloneChatActive && (
          <div className={`search-policy-badge ${searchMemoryLimitInfo.mode}`}>
            {searchMemoryLimitInfo.mode === "default"
              ? `Search policy: top ${searchMemoryLimitInfo.limit} (default)`
              : `Search policy: top ${searchMemoryLimitInfo.limit} (override)`}
          </div>
        )}

        {!standaloneChatActive && (
          <button className="info-preview" onClick={() => setShowInfo(true)} aria-label="View system information">
            {lastToolCall ? (
              <span className="tool-preview">
                <span className="tool-icon">Tool:</span>
                {lastToolCall.length > 52 ? `${lastToolCall.slice(0, 52)}...` : lastToolCall}
              </span>
            ) : workingMemory ? (
              <span className="memory-preview">
                <span className="memory-icon">WM</span>
                Working memory loaded
              </span>
            ) : (
              <span className="muted-text">Tap for system info</span>
            )}
          </button>
        )}
      </main>

      {!standaloneChatActive && (
        <footer className="aida-footer">
          <div className="footer-actions">
            <button className="settings-btn" onClick={openThreadDrawer} aria-label="Open conversations" title="Conversations">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6.75h16M4 12h10M4 17.25h7" />
              </svg>
            </button>

            <button className="settings-btn" onClick={() => setShowSettings(true)} aria-label="Open settings" title="Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>

          <div className="footer-meta">
            <div
              className={`context-meter ${contextUsageLevel}`}
              title={formatContextUsageTitle(contextUsageView)}
              aria-label={formatContextUsageTitle(contextUsageView)}
            >
              <span className="context-meter-key">CTX</span>
              <span className="context-meter-value">{Math.round(contextUsageView.percentUsed)}%</span>
              <span className="context-meter-source">
                {contextUsageView.source === "gemini" ? "live" : "est"}
              </span>
            </div>
            <p className="copyright">Copyright Mike McClary 2026</p>
            <p className="build-meta">{APP_BUILD_LABEL}</p>
          </div>
        </footer>
      )}

      {!standaloneChatActive && (transcriptEntries.length > 0 || (connected && interactionMode === "text")) && (
        <div
          className={`conversation-transcript ${
            standaloneChatActive || (connected && interactionMode === "text") ? "text-mode" : ""
          }`}
        >
          <div className="conversation-transcript-header">
            <span>Conversation</span>
            <button
              onClick={() => {
                setTranscriptEntries([]);
                setWebSources([]);
                transcriptIdRef.current = 0;
              }}
              aria-label="Clear conversation transcript"
            >
              Clear
            </button>
          </div>
          <div className="conversation-transcript-list">
            {transcriptEntries.map((entry) => (
              <div
                className={`conversation-entry ${entry.role}${entry.partial ? " partial" : ""}`}
                key={entry.id}
              >
                <div className="conversation-entry-label">
                  {entry.role === "user" ? "You" : "AIDA"}
                </div>
                <div className="conversation-entry-text">{entry.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {webSources.length > 0 && (
        <section className="web-sources" aria-label="Web search sources">
          <div className="web-sources-header">Web sources</div>
          {webSources.map((search, searchIndex) => (
            <div className="web-search-result" key={`${search.query}-${searchIndex}`}>
              <div className="web-search-query">{search.query}</div>
              {search.sources.length > 0 ? (
                <div className="web-source-links">
                  {search.sources.map((source) => (
                    <a key={source.id || source.url} href={source.url} target="_blank" rel="noreferrer">
                      {source.title || source.url}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="web-search-empty">{search.message || search.status.replaceAll("_", " ")}</div>
              )}
            </div>
          ))}
        </section>
      )}

      {showDebugTranscript && (
        <div className="debug-transcript">
          <div className="debug-transcript-header">
            <span>Debug Transcript</span>
            <div className="debug-transcript-actions">
              <button onClick={() => setDebugEvents([])} aria-label="Clear debug transcript">
                Clear
              </button>
              <button onClick={() => handleDebugTranscriptToggle(false)} aria-label="Hide debug transcript">
                Hide
              </button>
            </div>
          </div>
          <div className="debug-transcript-list">
            {debugEvents.length === 0 ? (
              <div className="debug-line muted">No events yet.</div>
            ) : (
              debugEvents.slice().reverse().map((entry, idx) => (
                <div className={`debug-line ${entry.type}`} key={`${entry.ts}-${idx}`}>
                  <span className="debug-time">{new Date(entry.ts).toLocaleTimeString()}</span>
                  <span className="debug-type">{entry.type}</span>
                  <span className="debug-text">{entry.text}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showThreadDrawer && (
        <>
          <div className="thread-drawer-backdrop" onClick={() => setShowThreadDrawer(false)} />
          <aside className="thread-drawer" aria-label="AIDA conversations">
            <div className="thread-drawer-header">
              <div>
                <h3>Conversations</h3>
                <p>{canSwitchThreads ? "Choose where AIDA should continue." : "Stop the live session before switching."}</p>
              </div>
              <button
                className="thread-drawer-close"
                onClick={() => setShowThreadDrawer(false)}
                aria-label="Close conversations"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="thread-current">
              <div className="thread-section-label">Current</div>
              <div className="thread-current-card">
                <div className="thread-title">{getThreadTitle(activeThread)}</div>
                <div className="thread-meta-row">
                  <span>{activeSurface}</span>
                  <span>{formatThreadDate(activeConversation?.last_activity_at)}</span>
                </div>
                {activeConversation?.conversation_id && (
                  <div className="thread-id">{activeConversation.conversation_id}</div>
                )}
              </div>
            </div>

            <div className="thread-list-header">
              <span className="thread-section-label">Recent</span>
              <button
                className="thread-refresh"
                onClick={loadConversationState}
                disabled={conversationStateLoading}
                type="button"
              >
                Refresh
              </button>
            </div>

            {conversationStateError && (
              <div className="thread-error">{conversationStateError}</div>
            )}

            <div className="thread-list">
              {conversationStateLoading && recentThreads.length === 0 ? (
                <div className="thread-empty">Loading conversations...</div>
              ) : recentThreads.length === 0 ? (
                <div className="thread-empty">No recent AIDA conversations found.</div>
              ) : (
                recentThreads.map((thread) => {
                  const isActive = Boolean(thread.document_id && thread.document_id === activeDocumentId);
                  const badges = getSurfaceBadges(thread);
                  return (
                    <button
                      className={`thread-card ${isActive ? "active" : ""}`}
                      key={thread.document_id || thread.external_id || thread.title}
                      onClick={() => void handleSwitchThread(thread)}
                      disabled={!canSwitchThreads || isActive || switchingDocumentId === thread.document_id}
                      type="button"
                    >
                      <div className="thread-card-main">
                        <div className="thread-title">{getThreadTitle(thread)}</div>
                        {thread.summary && <div className="thread-summary">{thread.summary}</div>}
                        <div className="thread-meta-row">
                          <span>{formatThreadDate(thread.last_interaction_at || thread.started_at)}</span>
                          {typeof thread.message_count === "number" && <span>{thread.message_count} messages</span>}
                        </div>
                      </div>
                      <div className="thread-card-side">
                        {isActive ? (
                          <span className="thread-active-pill">Active</span>
                        ) : (
                          <span className="thread-action-pill">
                            {switchingDocumentId === thread.document_id ? "Switching" : "Continue"}
                          </span>
                        )}
                        {badges.length > 0 && (
                          <div className="thread-badges">
                            {badges.map((badge) => (
                              <span key={badge}>{badge}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>
        </>
      )}

      <InfoModal
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        lastToolCall={lastToolCall || statusMessage}
        workingMemoryLength={workingMemory.length}
      />

      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        selectedVoice={selectedVoice}
        onVoiceChange={setSelectedVoice}
        selectedVoiceRuntime={selectedVoiceRuntime}
        availableVoiceRuntimes={runtimeConfig.availableVoiceRuntimes}
        onVoiceRuntimeChange={(runtime) => {
          setSelectedVoiceRuntime(runtime);
          localStorage.setItem("aidaVoiceRuntime", runtime);
        }}
        selectedOpenAIRealtimeVoice={selectedOpenAIRealtimeVoice}
        availableOpenAIRealtimeVoices={availableOpenAIRealtimeVoices}
        onOpenAIRealtimeVoiceChange={(voice) => {
          setSelectedOpenAIRealtimeVoice(voice);
          localStorage.setItem("aidaOpenAIRealtimeVoice", voice);
        }}
        voiceSessionActive={connected || connecting || recovering}
        visualizerMode={visualizerMode}
        onVisualizerModeChange={handleVisualizerModeChange}
        showDebugTranscript={showDebugTranscript}
        onShowDebugTranscriptChange={handleDebugTranscriptToggle}
      />
    </div>
  );
}

function AuthenticatedApp() {
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig>(DEFAULT_RUNTIME_CONFIG);

  useEffect(() => {
    let cancelled = false;

    async function fetchRuntimeConfig() {
      try {
        const response = await fetch("/api/runtime-config");
        if (!response.ok) {
          throw new Error(`Runtime config request failed: ${response.status}`);
        }
        const data = (await response.json()) as Partial<RuntimeConfig>;
        if (cancelled) {
          return;
        }
        setRuntimeConfig({
          audioInitialBufferMs:
            typeof data.audioInitialBufferMs === "number" &&
            Number.isFinite(data.audioInitialBufferMs) &&
            data.audioInitialBufferMs > 0
              ? Math.floor(data.audioInitialBufferMs)
              : DEFAULT_RUNTIME_CONFIG.audioInitialBufferMs,
          outputPrimeOnConnect:
            typeof data.outputPrimeOnConnect === "boolean"
              ? data.outputPrimeOnConnect
              : DEFAULT_RUNTIME_CONFIG.outputPrimeOnConnect,
          carStartTestEnabled:
            typeof data.carStartTestEnabled === "boolean"
              ? data.carStartTestEnabled
              : DEFAULT_RUNTIME_CONFIG.carStartTestEnabled,
          availableVoiceRuntimes: Array.isArray(data.availableVoiceRuntimes)
            ? data.availableVoiceRuntimes.filter(
                (runtime): runtime is VoiceRuntime => isVoiceRuntime(runtime)
              )
            : DEFAULT_RUNTIME_CONFIG.availableVoiceRuntimes,
          openAIRealtimeVoices: Array.isArray(data.openAIRealtimeVoices)
            ? data.openAIRealtimeVoices.filter(
                (voice): voice is string => typeof voice === "string"
              )
            : DEFAULT_RUNTIME_CONFIG.openAIRealtimeVoices,
          voiceRuntimeAvailabilityKnown: true,
        });
      } catch (error) {
        if (!cancelled) {
          console.warn("[AIDA] Failed to load runtime config, using defaults:", error);
          setRuntimeConfig({
            ...DEFAULT_RUNTIME_CONFIG,
            voiceRuntimeAvailabilityKnown: true,
          });
        }
      }
    }

    void fetchRuntimeConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <LiveAPIProvider
      audioInitialBufferMs={runtimeConfig.audioInitialBufferMs}
      outputPrimeOnConnect={runtimeConfig.outputPrimeOnConnect}
    >
      <AidaInterface runtimeConfig={runtimeConfig} />
    </LiveAPIProvider>
  );
}

function App() {
  return (
    <AuthGate>
      <AuthenticatedApp />
    </AuthGate>
  );
}

export default App;
